/**
 * SG-14196 — is this web-app window framed inside a host app?
 *
 * Two surfaces render this same web-app inside a Google Sheets frame. The sidebar /
 * builder boots with `?app=addon`, which becomes `state.app.isAddon`. The Account
 * modal (`showModalDialog`) boots at /account carrying `context=account-management`
 * and DELIBERATELY omits `app=addon`.
 *
 * The omission is not an oversight. as-add-on's `api-user.ts` appends `app=addon`
 * inside `if (!isAccountManagement)`, so the modal renders the roomy PanelMain2024
 * shell in its ~1150px dialog instead of the 300px sidebar shell. `isAddon` is
 * therefore answering "which shell?", and for the modal that answer must stay false.
 *
 * "Is it framed?" is a different question, and the two only diverge here. Stripe
 * Checkout refuses to be framed on BOTH surfaces, so anything that would navigate
 * the current window into checkout must key on THIS predicate, never on `isAddon`.
 *
 * ⚠️ The marker is read ONCE, at boot, into `state.app.isAccountManagementFrame`
 * (see redux/states/App) — never re-read from the live URL. Three call sites
 * bare-push PRICING and drop the query string (CheckoutPage, LayoutHOC,
 * TrialEndedFreeBanner), so a guard re-reading `window.location` would find the
 * marker gone at exactly the moment a route guard needs it. `isAddon` is a
 * boot-captured store flag for the same reason — which is why the route-level
 * guard already holds for the sidebar.
 *
 * Kept as a leaf module — it imports only a constant, so a guard can consume it
 * without dragging in env / connector-URL resolution.
 */
import { ACCOUNT_MANAGEMENT_CONTEXT } from '../constants/accountContext'
import { ACCOUNT, ARCHIVED_WORKFLOWS } from '../constants/routes'

/**
 * Whether a query string carries the Account-modal context marker.
 *
 * Boot-time use only — see the module note. Exported so `states/App` can capture it
 * once, and so the marker rule can be driven directly by tests.
 *
 * @param {string} [search] - Location search string, e.g. `?context=account-management`.
 * @returns {boolean} True when this window was opened as the add-on's Account modal.
 */
export const isAccountManagementSearch = (search) => (
    new URLSearchParams(search || '').get('context') === ACCOUNT_MANAGEMENT_CONTEXT
)

/**
 * Whether this window is framed inside a host app, on any add-on surface.
 *
 * @param {boolean} isAddon - Redux `state.app.isAddon`; true for the sidebar / builder.
 * @param {boolean} isAccountManagementFrame - Redux `state.app.isAccountManagementFrame`; boot-captured.
 * @returns {boolean} True when navigating the current window to Stripe Checkout would dead-end.
 */
export const isFramedSurface = (isAddon, isAccountManagementFrame) => (
    Boolean(isAddon) || Boolean(isAccountManagementFrame)
)

/**
 * The utm_source stamp for marketing links clicked on a framed surface.
 *
 * `docs/specs/tracking-params.md` — utm_source is the ORIGIN of the visit as
 * the destination's analytics sees it. The static constants are stamped
 * `app.sheetgo.com`, which is right for the plain web app; inside the add-on's
 * frames the visitor's origin is the add-on, whose long-established stamp is
 * `google-sheets-addon` (exclusive to the add-on, covering both its faces).
 */
export const FRAMED_MARKETING_UTM_SOURCE = 'google-sheets-addon'

/**
 * Make a marketing link's utm_source honest about the surface it was clicked
 * on: framed surfaces swap the static `utm_source=app.sheetgo.com` stamp for
 * `google-sheets-addon`. Unframed surfaces (and URLs without the app stamp —
 * e.g. legacy functional params like referrer_source, which are never
 * attribution) pass through untouched.
 *
 * @param {string} url - A marketing-link constant (utm-stamped or not).
 * @param {boolean} isAddon - Redux `state.app.isAddon`; boot-captured.
 * @param {boolean} isAccountManagementFrame - Redux `state.app.isAccountManagementFrame`; boot-captured.
 * @returns {string} The URL with a surface-true utm_source.
 */
export const resolveMarketingLinkForSurface = (url, isAddon, isAccountManagementFrame) => {
    if (typeof url !== 'string' || !isFramedSurface(isAddon, isAccountManagementFrame)) return url
    return url.replace('utm_source=app.sheetgo.com', `utm_source=${FRAMED_MARKETING_UTM_SOURCE}`)
}

const isAccountAreaPath = (pathname) => (
    typeof pathname === 'string' && (
        pathname === ACCOUNT ||
        pathname.startsWith(`${ACCOUNT}/`) ||
        pathname === ARCHIVED_WORKFLOWS
    )
)

/**
 * Keep the Account-modal context on a navigation inside the account area.
 *
 * `useIsIsolatedUI` reads `context=account-management` from the LIVE query string, so a
 * navigation to a bare path (a nav link, a `history.push(ARCHIVED_WORKFLOWS)`, a redirect)
 * drops the marker and the modal flips to the full shell (sidebar, bell). This copies the
 * marker and the `stm_*` tracking params from the current search onto an account-area
 * target that does not already name its own `context`. Anything else passes through
 * untouched: other paths, numeric history deltas, targets with their own context, and
 * windows that are not the Account modal.
 *
 * @param {string|object|number} to - Navigation target (path string or `{ pathname, search, hash }`).
 * @param {string} [currentSearch] - The current location search string.
 * @returns {string|object|number} The target, carrying the context when it applies.
 */
export const carryAccountContext = (to, currentSearch) => {
    if (!isAccountManagementSearch(currentSearch)) return to
    const isString = typeof to === 'string'
    if (isString && (to.includes('\\') || !to.startsWith('/') || to.startsWith('//'))) return to
    if (!isString && !(to && typeof to === 'object')) return to
    const url = isString ? new URL(to, 'http://_placeholder_') : null
    const pathname = isString ? url.pathname : to.pathname
    const targetSearch = isString ? url.search : (to.search || '')
    if (!isAccountAreaPath(pathname)) return to
    const params = new URLSearchParams(targetSearch)
    if (params.has('context')) return to
    const carried = new URLSearchParams(currentSearch)
    const next = new URLSearchParams()
    next.set('context', ACCOUNT_MANAGEMENT_CONTEXT)
    carried.forEach((value, key) => {
        if (key.startsWith('stm_') && !params.has(key)) next.set(key, value)
    })
    params.forEach((value, key) => next.append(key, value))
    const search = `?${next.toString()}`
    return isString
        ? `${url.pathname}${search}${url.hash}`
        : { ...to, search }
}
